import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { getApiErrorMessage } from '../utils/errorMessage';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './Layout/LanguageSelector';
import { persistRedirectFromUrl, getRedirectUrl, getReturnPath } from '../utils/authRedirect';
import './Auth.css';

const Login = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [searchParams] = useSearchParams();

  // Persist redirect/returnPath from URL to sessionStorage and show clean URL in address bar
  useEffect(() => {
    persistRedirectFromUrl(searchParams, '/login');
  }, [searchParams]);

  const redirectUrl = getRedirectUrl(searchParams);
  const returnPath = getReturnPath(searchParams);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleResendVerification = async () => {
    if (!formData.email) return;
    setResendSuccess('');
    setResendLoading(true);
    try {
      const res = await api.post('/auth/resend-verification', { email: formData.email });
      setResendSuccess(res?.data?.message || t('auth.verificationEmailSent', 'Verification email sent. Check your inbox—and your spam/junk folder—and make sure you\'re checking the same email you registered with.'));
      setError('');
    } catch (err) {
      setResendSuccess('');
      setError(getApiErrorMessage(err, t('auth.resendFailed', 'Failed to send verification email.')));
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendSuccess('');
    setEmailNotVerified(false);
    setLoading(true);

    if (!formData.email || !formData.password) {
      setError(t('auth.emailRequired', 'Please enter email and password.'));
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });

      if (response.data.success) {
        const { user, token } = response.data.data;

        // Store authentication data in localStorage
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userRole', user.role);
        localStorage.setItem('userId', user.id);
        localStorage.setItem('token', token);
        localStorage.setItem('userData', JSON.stringify(user));

        // Determine redirect path based on role.
        // Only dashboard routes exist in auth-app; public routes like "/contact" are on the main frontend.
        const allowedAuthAppPaths = new Set(['/admin', '/cleaner', '/client']);

        let finalPath = returnPath;
        const normalizedReturnPath = (returnPath || '/').split('?')[0].split('#')[0];

        // If returnPath isn't a dashboard route, always go to the role dashboard
        if (!allowedAuthAppPaths.has(normalizedReturnPath)) {
          finalPath =
            user.role === 'admin'
              ? '/admin'
              : user.role === 'cleaner'
                ? '/cleaner'
                : '/client';
        }

        // Store user data in localStorage
        localStorage.setItem('userData', JSON.stringify(user));

        // Redirect directly to dashboard in auth-app
        window.location.href = finalPath;
      } else {
        setError(response.data.message || t('auth.loginError', 'Invalid email or password. Please try again.'));
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorCode = err.response?.data?.code;
      const errorMessage = getApiErrorMessage(err, '');
      const statusCode = err.response?.status;

      if (errorCode === 'EMAIL_NOT_VERIFIED' || statusCode === 403 || /verify|verification/i.test(errorMessage || '')) {
        setEmailNotVerified(true);
        setError(t('auth.verifyEmailFirst', 'Please verify your email address before logging in. Check your inbox or use the resend option below.'));
      } else {
        setError(errorMessage || t('auth.loginError', 'Invalid email or password. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-header-row">
        <LanguageSelector />
      </div>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo-container">
            <img src="/images/logo-Photoroom.png" alt="Cleaning Service Logo" className="auth-logo" />
          </div>
          <h2>{t('auth.login', 'Login')}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('auth.email', 'Email')}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('auth.password', 'Password')}</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>

            <div style={{ textAlign: 'right', marginBottom: '15px' }}>
              <Link 
                to="/forgot-password"
                style={{ 
                  color: '#2c7a7b', 
                  textDecoration: 'none', 
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {t('auth.forgotPassword', 'Forgot Password?')}
              </Link>
            </div>

            {error && <div className="error-message">{error}</div>}
            {resendSuccess && <div className="success-message">{resendSuccess}</div>}
            {emailNotVerified && (
              <div style={{ marginBottom: '12px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  style={{ width: '100%', padding: '10px' }}
                >
                  {resendLoading ? t('auth.sending', 'Sending...') : t('auth.resendVerification', 'Resend verification email')}
                </button>
              </div>
            )}

            <button 
              type="submit" 
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? t('auth.loggingIn', 'Logging in...') : t('auth.login', 'Login')}
            </button>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <p style={{ color: '#666', fontSize: '14px' }}>
                {t('auth.dontHaveAccount', "Don't have an account?")}{' '}
                <Link
                  to="/register"
                  style={{ color: '#2c7a7b', textDecoration: 'none', fontWeight: '600' }}
                >
                  {t('auth.registerHere', 'Register here')}
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;

